import React, { type InputHTMLAttributes, type ButtonHTMLAttributes, type ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useKeyboardShortcut } from './hooks/useKeyboardShortcut';
import { useIsMobile } from './hooks/useIsMobile';
import { LoadingSpinner } from './components/LoadingSpinner';
import { COLORS } from './colors';

/**
 * Standard page header styles for consistent design across all main pages.
 * Use this for page titles to ensure uniformity.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const PAGE_HEADER_STYLES = {
  marginTop: '1.5rem',
  marginBottom: '1.5rem',
};

// A wrapper for the entire form card, providing the background and centering.
export function FormCard({ title, children }: { title: string, children: ReactNode }) {
  return (
    // Outer container with uniform background image matching protected pages
    <div
      className="min-h-screen flex flex-col justify-center items-center font-sans"
      style={{
        backgroundImage: 'url(/assets/Background_Horizontal.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
        paddingLeft: '1.5rem',
        paddingRight: '1.5rem',
        paddingTop: '1rem',
        paddingBottom: '1rem'
      }}
    >
      {/* White card container matching SignIn/SignUp design */}
      <div className="max-w-lg w-full bg-white rounded-lg shadow-xl" style={{ padding: '3rem 4rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Title matching auth pages: large, centered, gray-900 */}
        <h1 className="text-5xl font-normal text-center text-gray-900" style={{ marginBottom: '0.5rem' }}>{title}</h1>
        {/* Children (form, links, etc.) are rendered here */}
        {children}
      </div>
    </div>
  );
}

/**
 * A reusable white card container for general page content.
 * Provides consistent spacing and layout for CMS, Friends, Config, and similar pages.
 */
export function ContentCard({ title, children }: { title: string, children: ReactNode }) {
    return (
        <div className="container mx-auto p-4 sm:p-8 relative">
             <h1 className="text-4xl font-normal text-white" style={PAGE_HEADER_STYLES}>{title}</h1>
             <div className="bg-white p-6 rounded-2xl shadow-xl">
                {children}
             </div>
        </div>
    );
}

// A styled text input field with its label.
interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  variant?: 'default' | 'underline';
}
export function Input({ label, variant = 'default', ...props }: InputProps) {
  const inputClassName = variant === 'underline'
    ? "w-full px-0 py-3 bg-transparent border-0 border-b-2 border-gray-300 text-gray-900 focus:outline-none focus:border-gray-900 disabled:bg-gray-100"
    : "w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 disabled:bg-gray-100";

  const labelClassName = variant === 'underline'
    ? "block text-base font-normal text-gray-500 mb-2"
    : "block text-sm font-medium text-gray-700 mb-2";

  return (
    <div>
      <label htmlFor={props.id} className={labelClassName}>
        {label}
      </label>
      <input
        {...props}
        className={inputClassName}
      />
    </div>
  );
}

// The main action button for the form.
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    children: ReactNode;
    variant?: 'brown';
}
export function Button({ children, ...props }: ButtonProps) {
  const [isHovered, setIsHovered] = React.useState(false);

  // Match Remove button style - colors imported from COLORS (single source of truth)
  const buttonStyle: React.CSSProperties = {
    width: '100%',
    backgroundColor: props.disabled ? COLORS.primary[400] : (isHovered ? COLORS.primary[700] : COLORS.primary[600]),
    color: COLORS.text.inverse,
    padding: '8px 16px',
    fontSize: '14px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: 500,
    cursor: props.disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.2s',
    opacity: props.disabled ? 0.5 : 1,
  };

  return (
    <button
      {...props}
      style={{ ...buttonStyle, ...props.style }}
      onMouseEnter={() => !props.disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
    </button>
  );
}

// A styled link for secondary actions like "Forgot Password?".
export function ActionLink({ children, ...props }: { children: ReactNode } & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
    return (
        <a {...props} className="font-medium text-blue-500 hover:text-blue-600 text-center block text-sm">
            {children}
        </a>
    );
}

// A container for the final action link, like "Don't have an account?".
export function SecondaryAction({ children }: { children: ReactNode }) {
    return (
        <div className="text-center text-sm text-gray-500 pt-4 border-t border-gray-200">
            {children}
        </div>
    );
}

// Reusable editable cell component for DataGrid tables
// Supports Enter to save, Escape to cancel
// Preserves cursor position when editing in the middle of text
interface EditableCellProps {
    value: string;
    isEditing: boolean;
    onEdit: () => void;
    onSave: () => void;
    onCancel: () => void;
    onChange: (value: string) => void;
    displayValue?: ReactNode; // Optional custom display when not editing
    showPenIcon?: boolean; // Show edit pen icon (default: true)
    inputClassName?: string; // Custom input styling
}
export function EditableCell({
    value,
    isEditing,
    onEdit,
    onSave,
    onCancel,
    onChange,
    displayValue,
    showPenIcon = true,
    inputClassName = "w-full p-1 border rounded-md bg-white text-gray-900"
}: EditableCellProps) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const cursorPositionRef = React.useRef<number | null>(null);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
            onSave();
        } else if (e.key === 'Escape') {
            onCancel();
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Store cursor position before the change
        cursorPositionRef.current = e.target.selectionStart;
        onChange(e.target.value);
    };

    // Focus the input and select all text when entering edit mode
    // This allows the user to start typing immediately or click to position cursor
    React.useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    // Restore cursor position after value changes
    React.useEffect(() => {
        if (isEditing && inputRef.current && cursorPositionRef.current !== null) {
            inputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
        }
    }, [value, isEditing]);

    if (isEditing) {
        return (
            <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className={inputClassName}
            />
        );
    }

    return (
        <div className="flex items-center gap-2">
            <span>{displayValue || value}</span>
            {showPenIcon && (
                <button
                    onClick={onEdit}
                    className="text-gray-500 hover:text-primary-600 transition-colors"
                    title="Edit"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                    </svg>
                </button>
            )}
        </div>
    );
}

// Reusable cover upload cell component for DataGrid tables
// Used for album and video covers
interface CoverUploadCellProps {
    itemId: string;
    coverURL?: string;
    autoThumbnailURL?: string; // Auto-generated thumbnail from video upload
    defaultCoverURL?: string; // Optional default cover to show when coverURL is not set
    isUploading: boolean;
    onUpload: (itemId: string, file: File) => void;
}
export function CoverUploadCell({
    itemId,
    coverURL,
    autoThumbnailURL,
    defaultCoverURL,
    isUploading,
    onUpload
}: CoverUploadCellProps) {
    const [imageError, setImageError] = React.useState(false);
    const [imageKey, setImageKey] = React.useState(0);
    const [lastCoverURL, setLastCoverURL] = React.useState(coverURL);
    const hasCover = !!coverURL && !imageError;

    // Priority: custom cover > auto-generated thumbnail > default cover
    const displayImageURL = coverURL || autoThumbnailURL || defaultCoverURL;

    // Reset image error when coverURL changes and force reload
    React.useEffect(() => {
        if (coverURL && coverURL !== lastCoverURL) {
            setImageError(false);
            setImageKey(prev => prev + 1);
            setLastCoverURL(coverURL);
        }
    }, [coverURL, lastCoverURL]);

    return (
        <div className="flex items-center gap-2">
            {/* Show loading state while uploading */}
            {isUploading && (
                <div className="w-10 h-10 bg-primary-100 rounded flex items-center justify-center">
                    <LoadingSpinner size="md" style={{ color: COLORS.primary.DEFAULT }} />
                </div>
            )}

            {/* Show image (custom cover or auto-generated thumbnail) when not uploading */}
            {!isUploading && displayImageURL && !imageError && (
                <img
                    key={`${itemId}-${imageKey}`}
                    src={displayImageURL}
                    alt="Cover"
                    className="w-10 h-10 object-cover rounded"
                    onError={() => {
                        setImageError(true);
                    }}
                />
            )}

            {/* Show error placeholder when image fails and not uploading */}
            {!isUploading && displayImageURL && imageError && (
                <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center" title={`Failed to load: ${displayImageURL}`}>
                    <span className="text-xs text-gray-500">?</span>
                </div>
            )}

            <label
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    isUploading
                        ? 'bg-primary-100 text-primary-400 cursor-not-allowed'
                        : 'bg-primary-200 text-primary-800 hover:bg-primary-300 cursor-pointer'
                }`}
            >
                {isUploading ? 'Uploading...' : (autoThumbnailURL || hasCover) ? 'Change' : 'Upload'}
                <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={isUploading}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                            setImageError(false); // Reset error on new upload
                            onUpload(itemId, file);
                        }
                        // Reset the input so the same file can be selected again
                        e.target.value = '';
                    }}
                />
            </label>
        </div>
    );
}

// Reusable Modal component for displaying popup dialogs
interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    theme?: 'light' | 'dark';
    showCloseButton?: boolean;
    backdropOpacity?: number;
    title?: string | ReactNode;
    className?: string; // Additional custom classes for the modal content
}

export function Modal({
    isOpen,
    onClose,
    children,
    size = 'md',
    theme = 'light',
    showCloseButton = false,
    backdropOpacity = 0.15,
    title,
    className = ''
}: ModalProps) {
    // Handle ESC key to close modal
    useKeyboardShortcut({
        onEscape: onClose,
        enabled: isOpen,
        preventDefault: false,
    });

    if (!isOpen) return null;

    // Size variants
    const sizeClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-4xl',
        xl: 'max-w-6xl'
    };

    // Theme variants
    const themeClasses = {
        light: {
            content: 'bg-white text-gray-800',
            closeButton: 'text-gray-400 hover:text-gray-600'
        },
        dark: {
            content: 'bg-gray-900 text-white',
            closeButton: 'text-gray-400 hover:text-gray-200'
        }
    };

    // Use createPortal to render modal at document.body level
    // This ensures modals escape any parent CSS transforms that would break position:fixed
    return createPortal(
        <div
            className="fixed inset-0 flex items-center justify-center z-50"
            style={{ backgroundColor: `rgba(0, 0, 0, ${backdropOpacity})` }}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
            }}
        >
            <div
                className={`relative ${themeClasses[theme].content} rounded-lg shadow-2xl ${sizeClasses[size]} w-full mx-4 ${className}`}
                onClick={(e) => {
                    e.stopPropagation();
                }}
                style={{ cursor: 'default' }}
            >
                {/* Close button */}
                {showCloseButton && (
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onClose();
                        }}
                        className={`absolute top-6 right-6 ${themeClasses[theme].closeButton} text-3xl font-bold z-10`}
                        title="Close"
                    >
                        ✕
                    </button>
                )}

                {/* Optional title */}
                {title && (
                    <div style={{ paddingLeft: '40px', paddingRight: '40px', paddingTop: '24px', paddingBottom: '16px' }}>
                        <h2 className="text-2xl font-bold">{title}</h2>
                    </div>
                )}

                {/* Modal content */}
                {children}
            </div>
        </div>,
        document.body
    );
}

// User data type used in user management tables
export type UserData = {
    uid: string;
    email: string;
    firstName: string;
    lastName: string;
    createdAt: { seconds: number; nanoseconds: number } | Date | null; // Firestore Timestamp
    updatedAt: { seconds: number; nanoseconds: number } | Date | null; // Firestore Timestamp
    roles: string[];
    status: 'active' | 'inactive' | 'disabled';
};

// Reusable User Management Table component
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

interface UserManagementTableProps {
    users: UserData[];
    columns: GridColDef[];
    noRowsMessage: string;
    editingUid: string | null;
    onCellClick?: (params: { id: string | number; field: string; row: UserData }, event: React.MouseEvent) => void;
}

function CustomNoRowsOverlay({ message }: { message: string }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <div className="bg-gray-800/80 backdrop-blur-sm px-6 py-4 rounded-lg">
                <p className="text-white font-medium text-center">{message}</p>
            </div>
        </div>
    );
}

export function UserManagementTable({
    users,
    columns,
    noRowsMessage,
    onCellClick
}: UserManagementTableProps) {
    return (
        <div className="mt-8" style={{ width: '100%' }}>
            <DataGrid
                rows={users}
                columns={columns}
                getRowId={(row) => row.uid}
                slots={{ noRowsOverlay: () => <CustomNoRowsOverlay message={noRowsMessage} /> }}
                onCellClick={onCellClick}
                autoHeight
            />
        </div>
    );
}

// Reusable SearchBar component for mobile layouts
interface SearchBarProps {
    placeholder?: string;
    value?: string;
    onChange?: (value: string) => void;
    className?: string;
}

export function SearchBar({
    placeholder = "Search...",
    value = "",
    onChange = () => {},
    className = ""
}: SearchBarProps) {
    return (
        <div className={`relative ${className}`}>
            <input
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full pr-12 bg-white border border-gray-300 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400"
                style={{ paddingTop: '14px', paddingBottom: '14px', paddingLeft: '16px' }}
            />
            <svg
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
        </div>
    );
}

// Reusable ActionButton component for mobile layouts
interface ActionButtonProps {
    children: ReactNode;
    onClick?: () => void;
    color?: 'purple' | 'cyan' | 'gray' | 'blue';
    className?: string;
    disabled?: boolean;
}

export function ActionButton({
    children,
    onClick = () => {},
    color = 'purple',
    className = "",
    disabled = false
}: ActionButtonProps) {
    const colorClasses = {
        purple: 'bg-purple-600 hover:bg-purple-700 text-white',
        cyan: 'bg-black/30 hover:bg-black/50 text-white',
        gray: 'bg-gray-600 hover:bg-gray-700 text-white',
        blue: 'bg-blue-600 hover:bg-blue-700 text-white'
    };

    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`w-full rounded-lg font-semibold transition-colors text-sm sm:text-base ${colorClasses[color]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
            style={{ paddingTop: '14px', paddingBottom: '14px', paddingLeft: '12px', paddingRight: '12px' }}
        >
            {children}
        </button>
    );
}

// Reusable MobilePageHeader component for consistent mobile page layouts
// Used by Albums, AlbumPage, and potentially other pages
interface MobilePageHeaderProps {
    title: string;
    searchPlaceholder?: string;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    showSearch?: boolean;
    actions?: Array<{
        label: string;
        color: 'purple' | 'cyan' | 'gray' | 'blue';
        onClick: () => void;
        disabled?: boolean;
    }>;
    className?: string;
    rightIcon?: React.ReactNode;
}

export function MobilePageHeader({
    title,
    searchPlaceholder = "Search...",
    searchValue = "",
    onSearchChange = () => {},
    showSearch = true,
    actions = [],
    className = "",
    rightIcon
}: MobilePageHeaderProps) {
    // Responsive values based on screen width - use smaller values on narrow screens
    const isSmallScreen = useIsMobile(375); // Use 375px breakpoint for extra small screens
    const titleMarginTop = isSmallScreen ? '80px' : '120px';
    const horizontalPadding = isSmallScreen ? '24px' : '32px';
    const buttonGap = isSmallScreen ? '8px' : '12px';
    const bottomMargin = '24px';

    return (
        <div className={`flex flex-col ${className}`} style={{ gap: '32px', marginBottom: '24px' }}>
            {/* Title with responsive margin and optional right icon */}
            <div className="relative">
                <h1 className="text-3xl font-normal text-white text-center" style={{ marginTop: titleMarginTop }}>
                    {title}
                </h1>
                {rightIcon && (
                    <div className="absolute right-6 top-0" style={{ marginTop: titleMarginTop }}>
                        {rightIcon}
                    </div>
                )}
            </div>

            {showSearch && (
                <div style={{ paddingLeft: horizontalPadding, paddingRight: horizontalPadding }}>
                    <SearchBar
                        placeholder={searchPlaceholder}
                        value={searchValue}
                        onChange={onSearchChange}
                    />
                </div>
            )}

            {actions.length > 0 && (
                <div className="flex flex-row" style={{ gap: buttonGap, paddingLeft: horizontalPadding, paddingRight: horizontalPadding, marginBottom: bottomMargin }}>
                    {actions.map((action, index) => (
                        <ActionButton
                            key={index}
                            color={action.color}
                            onClick={action.onClick}
                            disabled={action.disabled}
                        >
                            {action.label}
                        </ActionButton>
                    ))}
                </div>
            )}
        </div>
    );
}

// Reusable gradient overlay for thumbnail visibility - smoother fade, no visible edge
export function ThumbnailGradientOverlay() {
    return (
        <div
            className="absolute pointer-events-none"
            style={{
                top: 0,
                left: 0,
                width: '50%',
                height: '50%',
                background: 'radial-gradient(ellipse at top left, rgba(0,0,0,0.40) 0%, rgba(0,0,0,0.28) 20%, rgba(0,0,0,0.16) 40%, rgba(0,0,0,0.06) 55%, transparent 70%)'
            }}
        />
    );
}

// Reusable VideographerBadge component - displays "Flm by [name]" and opens contact modal on click
interface VideographerBadgeProps {
    videographerId: string;
    videographerName?: string; // Optional - if already available, avoids fetching
    videographerEmail?: string; // Optional - if already available, avoids fetching
    variant?: 'album-cover-mobile' | 'album-cover-desktop' | 'video-mobile' | 'video-desktop'; // Position variant
    className?: string; // Additional positioning classes (overrides variant)
    style?: React.CSSProperties; // Additional inline styles (overrides variant)
}

export function VideographerBadge({
    videographerId,
    videographerName: providedName,
    videographerEmail: providedEmail,
    variant,
    className,
    style
}: VideographerBadgeProps) {
    // Define positioning based on variant
    const getPositionStyle = (): React.CSSProperties => {
        if (style) return style; // Custom style overrides everything

        const positions = {
            'album-cover-mobile': { position: 'absolute' as const, top: '0.5rem', left: '0.5rem' },
            'album-cover-desktop': { position: 'absolute' as const, top: '3.5rem', left: '0.75rem', pointerEvents: 'auto' as const },
            'video-mobile': { position: 'absolute' as const, top: '0.35rem', left: '0.35rem' },
            'video-desktop': { position: 'absolute' as const, top: '0.5rem', left: '0.5rem' },
        };

        return variant ? positions[variant] : { position: 'absolute' as const, top: '0.5rem', left: '0.5rem' };
    };
    const [videographerName, setVideographerName] = useState<string | null>(providedName || null);
    const [videographerEmail, setVideographerEmail] = useState<string | null>(providedEmail || null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(!providedName || !providedEmail);

    // Fetch videographer data if not provided
    useEffect(() => {
        if (providedName && providedEmail) {
            setVideographerName(providedName);
            setVideographerEmail(providedEmail);
            setIsLoading(false);
            return;
        }

        const fetchVideographer = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'users', videographerId));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setVideographerName(`${userData.firstName} ${userData.lastName}`);
                    setVideographerEmail(userData.email);
                }
            } catch (error) {
                console.error('Failed to fetch videographer:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchVideographer();
    }, [videographerId, providedName, providedEmail]);

    if (isLoading || !videographerName) {
        return null; // Don't render until data is loaded
    }

    return (
        <>
            <div className={className} style={getPositionStyle()}>
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsModalOpen(true);
                    }}
                    className="text-white drop-shadow-lg hover:text-gray-200 transition-colors text-left"
                    style={{
                        padding: 'clamp(0.4rem, 1.5vw, 0.75rem)'
                    }}
                >
                    <p
                        className="font-light"
                        style={{
                            fontSize: 'clamp(0.65rem, 2vw, 0.875rem)',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        Flm by
                    </p>
                    <p
                        className="font-semibold"
                        style={{
                            fontSize: 'clamp(0.9rem, 3vw, 1.25rem)',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {videographerName}
                    </p>
                </button>
            </div>

            {/* Videographer Contact Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                size="md"
                theme="light"
                showCloseButton={true}
                backdropOpacity={0.5}
                title={`Contact ${videographerName}`}
            >
                <div style={{
                    paddingLeft: '40px',
                    paddingRight: '40px',
                    paddingTop: '0px',
                    paddingBottom: '30px',
                    maxWidth: '600px',
                    margin: '0 auto'
                }}>
                    {/* Contact info */}
                    <div className="w-full space-y-4">
                        {/* Email */}
                        {videographerEmail && (
                            <div className="flex items-center space-x-4 text-gray-800">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="w-6 h-6 text-gray-600"
                                >
                                    <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                                    <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
                                </svg>
                                <a href={`mailto:${videographerEmail}`} className="hover:text-primary-600 text-gray-900">
                                    {videographerEmail}
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    );
}

// Reusable header for album-related pages (Albums, AlbumPage)
// Provides title and sticky icon-based controls (search, favorites, edit, etc.)
interface AlbumPageHeaderProps {
    title: string;
    children?: ReactNode; // Icon controls go here
}

export function AlbumPageHeader({ title, children }: AlbumPageHeaderProps) {
    return (
        <>
            {/* Title */}
            <div style={{ paddingLeft: '24px', paddingRight: '24px', paddingTop: '32px', paddingBottom: '12px' }}>
                <h1 className="text-3xl font-normal text-white text-center">{title}</h1>
            </div>

            {/* Sticky Icon Controls */}
            {children && (
                <div style={{
                    position: 'sticky',
                    top: '16px',
                    zIndex: 40,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    justifyContent: 'flex-end',
                    paddingLeft: '24px',
                    paddingRight: '24px',
                    paddingBottom: '24px'
                }}>
                    {children}
                </div>
            )}
        </>
    );
}

// Reusable Icon Button component matching Albums.tsx design
// Used for floating action buttons and icon controls throughout the app
interface IconButtonProps {
    onClick: () => void;
    icon: ReactNode;
    title?: string; // Tooltip text
    className?: string; // Additional custom classes
    variant?: 'default' | 'primary' | 'purple' | 'secondary'; // Color variants
    size?: 'sm' | 'md' | 'lg'; // Size variants
    disabled?: boolean;
}

export function IconButton({
    onClick,
    icon,
    title,
    className = '',
    variant = 'default',
    size = 'md',
    disabled = false
}: IconButtonProps) {
    const [isHovered, setIsHovered] = React.useState(false);

    // Variant background colors - imported from COLORS (single source of truth)
    const variantStyles = {
        default: {
            backgroundColor: isHovered ? COLORS.overlay.medium : COLORS.overlay.light
        },
        primary: {
            backgroundColor: isHovered ? COLORS.primary[700] : COLORS.primary[600]
        },
        purple: {
            backgroundColor: isHovered ? COLORS.secondary[400] : COLORS.secondary[300]
        },
        secondary: {
            backgroundColor: isHovered ? COLORS.secondary[400] : COLORS.secondary[300]
        }
    };

    // Size variants
    const sizeStyles = {
        sm: { width: '40px', height: '40px' },
        md: { width: '48px', height: '48px' },
        lg: { width: '56px', height: '56px' }
    };

    return (
        <button
            onClick={onClick}
            disabled={disabled}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                ...variantStyles[variant],
                ...sizeStyles[size],
                color: COLORS.text.inverse,
                borderRadius: '50%',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                boxShadow: isHovered ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer'
            }}
            className={className}
            title={title}
        >
            {icon}
        </button>
    );
}

// Floating Action Button with Modal Dialog
// Positioned in top-right corner using absolute positioning (like album page icons)
interface FloatingActionButtonProps {
    onClick: () => void;
    isOpen: boolean;
    icon?: ReactNode; // Custom icon (defaults to + icon)
    modalTitle: string; // Title shown in the modal
    children: ReactNode; // Form content to display in modal
    buttonTitle?: string; // Tooltip text for the FAB
    variant?: 'default' | 'primary' | 'purple'; // Color variant
}

export function FloatingActionButton({
    onClick,
    isOpen,
    icon,
    modalTitle,
    children,
    buttonTitle = "Add new",
    variant = 'default'
}: FloatingActionButtonProps) {
    const defaultIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
    );

    return (
        <>
            {/* Action Button - Absolute positioning in top-right using shared IconButton */}
            <IconButton
                onClick={onClick}
                icon={icon || defaultIcon}
                title={buttonTitle}
                variant={variant}
                size="md"
                className="absolute top-6 right-6 z-50"
            />

            {/* Modal Dialog */}
            <Modal
                isOpen={isOpen}
                onClose={onClick}
                size="md"
                theme="light"
                showCloseButton={true}
                backdropOpacity={0.5}
                title={modalTitle}
            >
                <div style={{
                    paddingLeft: '40px',
                    paddingRight: '40px',
                    paddingTop: '0px',
                    paddingBottom: '30px',
                    maxWidth: '600px',
                    margin: '0 auto'
                }}>
                    {children}
                </div>
            </Modal>
        </>
    );
}

// Reusable Edit Mode Controls (Save/Cancel buttons)
// Used in Albums.tsx and AlbumPage.tsx to prevent code duplication
interface EditModeControlsProps {
    onSave: () => void;
    onCancel: () => void;
    isSaving: boolean;
    saveTitle?: string;
    cancelTitle?: string;
    size?: 'sm' | 'md';
}

export function EditModeControls({
    onSave,
    onCancel,
    isSaving,
    saveTitle = "Save (Enter)",
    cancelTitle = "Cancel (Esc)",
    size = 'md'
}: EditModeControlsProps) {
    const iconSize = size === 'sm' ? 'h-5 w-5' : 'h-6 w-6';

    return (
        <>
            <IconButton
                onClick={onSave}
                disabled={isSaving}
                title={saveTitle}
                variant="primary"
                size={size}
                icon={
                    isSaving ? (
                        <LoadingSpinner size="sm" />
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className={iconSize} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    )
                }
            />
            <IconButton
                onClick={onCancel}
                disabled={isSaving}
                title={cancelTitle}
                variant="default"
                size={size}
                icon={
                    <svg xmlns="http://www.w3.org/2000/svg" className={iconSize} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                }
            />
        </>
    );
}

// Reusable Search Input Component
// Used in Albums.tsx and AlbumPage.tsx to prevent code duplication
interface SearchInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    size?: 'sm' | 'md';
    className?: string;
}

export function SearchInput({
    value,
    onChange,
    placeholder = "Search...",
    isOpen,
    onOpen,
    onClose,
    size = 'md',
    className = ""
}: SearchInputProps) {
    const iconSize = size === 'sm' ? 'h-5 w-5' : 'h-6 w-6';

    if (!isOpen) {
        return (
            <IconButton
                onClick={onOpen}
                title="Search"
                variant="default"
                size={size}
                className={className}
                icon={
                    <svg xmlns="http://www.w3.org/2000/svg" className={iconSize} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                }
            />
        );
    }

    return (
        <div className={`flex items-center gap-2 bg-black/30 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg ${size === 'sm' ? 'flex-1' : ''} ${className}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className={`bg-transparent text-white placeholder-gray-300 outline-none ${size === 'sm' ? 'flex-1' : 'w-64'}`}
                autoFocus
            />
            <button
                onClick={onClose}
                className="text-white hover:text-gray-300"
                title="Close search"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
}

